import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { normalizeMediaUrl } from '@sweethome/utils';

export { normalizeMediaUrl };

/**
 * Combine class names with Tailwind merge so later utilities override earlier ones.
 */
export const cn = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const isBrowser = typeof window !== 'undefined';

export const noop = (): void => {};

export const initials = (value: string | null | undefined): string => {
  if (!value) return '?';
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${Array.from(parts[0]!)[0] ?? ''}${Array.from(parts[parts.length - 1]!)[0] ?? ''}`.toUpperCase();
};

import { parseProductDimensions, type ProductDimensions } from '@sweethome/types';

export type StructuredDimensions = ProductDimensions;

/**
 * Parses user input (string or object) into a Medusa Carrier Standard Structured Object.
 * Example: "215 x 170 x 120 cm" -> { length: 215, width: 170, height: 120 }
 */
export function parseDimensionsObject(input: unknown): StructuredDimensions | string | null {
  const parsed = parseProductDimensions(input);
  if (parsed) return parsed;
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

/**
 * Formats a Medusa Carrier Standard Structured Object or string into human-readable text.
 * Example: { length: 215, width: 170, height: 120 } -> "215 x 170 x 120 cm"
 */
export function formatDimensions(input: unknown): string {
  if (!input) return '';
  if (typeof input === 'string') return input;
  if (typeof input === 'object' && input !== null) {
    const obj = input as Record<string, unknown>;
    const parts: (string | number)[] = [];
    if (obj.length !== undefined && obj.length !== null && !isNaN(Number(obj.length)))
      parts.push(Number(obj.length));
    if (obj.width !== undefined && obj.width !== null && !isNaN(Number(obj.width)))
      parts.push(Number(obj.width));
    if (obj.height !== undefined && obj.height !== null && !isNaN(Number(obj.height)))
      parts.push(Number(obj.height));
    if (parts.length > 0) return `${parts.join(' x ')} cm`;
  }
  return '';
}

/**
 * Strips dangerous HTML from a string before rendering with dangerouslySetInnerHTML.
 * Uses the browser's DOMParser for robust sanitization — regex-based approaches
 * are fundamentally bypassable.
 * Client-side only — only call in browser components.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Remove dangerous elements
  const dangerousTags = ['script', 'iframe', 'style', 'object', 'embed', 'form', 'link', 'meta'];
  for (const tag of dangerousTags) {
    const elements = doc.querySelectorAll(tag);
    elements.forEach((el) => el.remove());
  }

  // Remove on* event attributes and javascript: hrefs from all elements
  const allElements = doc.querySelectorAll('*');
  allElements.forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (
        attr.name.startsWith('on') ||
        (['href', 'src', 'action'].includes(attr.name) &&
          /^\s*(javascript|data):/i.test(attr.value))
      ) {
        el.removeAttribute(attr.name);
      }
    }
  });

  return doc.body.innerHTML;
}
